/**
 * Custom Collection Sort Dropdown
 * Replaces native select element with custom dropdown functionality
 */
class CollectionSortDropdown {
  constructor(element) {
    this.dropdown = element;
    this.list = element.querySelector('.collection-sort-dropdown__list');
    this.items = element.querySelectorAll('.collection-sort-dropdown__item');
    this.selectedText = element.querySelector('.collection-sort-dropdown__selected');
    
    this.isOpen = false;
    this.currentValue = this.getCurrentValue();
    
    this.init();
  }

  init() {
    this.bindEvents();
    this.setInitialState();
  }

  bindEvents() {
    // Toggle dropdown on wrapper click
    this.dropdown.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.toggle();
    });

    // Handle item selection
    this.items.forEach(item => {
      item.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.selectItem(item);
      });

      // Keyboard navigation
      item.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.selectItem(item);
        }
      });
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!this.dropdown.contains(e.target)) {
        this.close();
      }
    });

    // Keyboard navigation
    this.dropdown.addEventListener('keydown', (e) => {
      switch (e.key) {
        case 'Enter':
        case ' ':
        case 'ArrowDown':
          e.preventDefault();
          this.open();
          this.focusFirstItem();
          break;
        case 'ArrowUp':
          e.preventDefault();
          this.open();
          this.focusLastItem();
          break;
        case 'Escape':
          this.close();
          this.dropdown.focus();
          break;
      }
    });

    // Handle arrow key navigation in list
    this.list.addEventListener('keydown', (e) => {
      const currentIndex = Array.from(this.items).indexOf(document.activeElement);
      
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          this.focusNextItem(currentIndex);
          break;
        case 'ArrowUp':
          e.preventDefault();
          this.focusPreviousItem(currentIndex);
          break;
        case 'Escape':
          this.close();
          this.dropdown.focus();
          break;
        case 'Tab':
          this.close();
          break;
      }
    });
  }

  setInitialState() {
    // Set initial selected item
    const selectedItem = this.dropdown.querySelector('[aria-selected="true"]');
    if (selectedItem) {
      this.updateSelectedText(selectedItem.textContent.trim());
    }
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.isOpen = true;
    this.dropdown.classList.add('is-open');
    this.dropdown.setAttribute('aria-expanded', 'true');
    
    // Focus first item
    this.focusFirstItem();
  }

  close() {
    this.isOpen = false;
    this.dropdown.classList.remove('is-open');
    this.dropdown.setAttribute('aria-expanded', 'false');
    
    // Remove focus from items
    this.items.forEach(item => item.setAttribute('tabindex', '-1'));
  }

  selectItem(item) {
    const value = item.getAttribute('data-value');
    const text = item.textContent.trim();
    
    // Update selected state
    this.items.forEach(i => i.setAttribute('aria-selected', 'false'));
    item.setAttribute('aria-selected', 'true');
    
    // Update display
    this.updateSelectedText(text);
    this.currentValue = value;
    
    // Close dropdown
    this.close();
    
    // Trigger change event
    this.triggerChangeEvent(value);
  }

  updateSelectedText(text) {
    this.selectedText.textContent = text;
  }

  triggerChangeEvent(value) {
    // Create and dispatch custom event
    const changeEvent = new CustomEvent('sortChange', {
      detail: { value: value },
      bubbles: true
    });
    
    this.dropdown.dispatchEvent(changeEvent);
  }

  getCurrentValue() {
    const selectedItem = this.dropdown.querySelector('[aria-selected="true"]');
    return selectedItem ? selectedItem.getAttribute('data-value') : this.dropdown.getAttribute('data-default-sortby');
  }

  getValue() {
    return this.currentValue;
  }

  setValue(value) {
    const item = this.dropdown.querySelector(`[data-value="${value}"]`);
    if (item) {
      this.selectItem(item);
    }
  }

  // Focus management methods
  focusFirstItem() {
    if (this.items.length > 0) {
      this.items[0].setAttribute('tabindex', '0');
      this.items[0].focus();
    }
  }

  focusLastItem() {
    if (this.items.length > 0) {
      this.items[this.items.length - 1].setAttribute('tabindex', '0');
      this.items[this.items.length - 1].focus();
    }
  }

  focusNextItem(currentIndex) {
    const nextIndex = currentIndex < this.items.length - 1 ? currentIndex + 1 : 0;
    this.items[currentIndex].setAttribute('tabindex', '-1');
    this.items[nextIndex].setAttribute('tabindex', '0');
    this.items[nextIndex].focus();
  }

  focusPreviousItem(currentIndex) {
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : this.items.length - 1;
    this.items[currentIndex].setAttribute('tabindex', '-1');
    this.items[prevIndex].setAttribute('tabindex', '0');
    this.items[prevIndex].focus();
  }
}

// Initialize dropdowns when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  const dropdowns = document.querySelectorAll('.collection-sort-dropdown');
  dropdowns.forEach(dropdown => {
    const instance = new CollectionSortDropdown(dropdown);
    // Store instance reference for external access
    dropdown._dropdownInstance = instance;
  });
});

// Export for use in other modules
window.CollectionSortDropdown = CollectionSortDropdown;
